import React, { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'

import { useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { NotesIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/NotesIcon'
import { ExerciseIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/illustrations/ExerciseIcon'
import { TaskListPanel } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/taskListPanel/TaskListPanel'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { actions as mixpanelActions, mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { CLIENT_ACTIVITIES } from '../../../common/constants/routingConstants'
import { getClientFullName } from '../../../common/utils/utils'
import { useAppDispatch } from '../../../data/store'
import { setSelectedClientId } from '../../../lyraTherapy/clients/clientDetails/data/ltClientSlice'
import { useOpenItemsFilterSections } from '../hooks/useOpenItemsFilterSections'
import { useSharedItemHoverActions } from '../hooks/useSharedItemHoverActions'
import { FilterableItem } from '../types'

export type ClientActivityItem = FilterableItem & {
  unreviewedCount: number
}

type ClientActivityPanelProps = {
  items: ClientActivityItem[]
  isLoading: boolean
  onItemCountChange: (count: number) => void
}

const ClientActivityPanel: React.FC<ClientActivityPanelProps> = ({ items, isLoading, onItemCountChange }) => {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { isPreferredNameEnabled } = useFlags()
  const getSharedHoverActions = useSharedItemHoverActions('client_activity')
  const { sharedSections, clearSharedFilters, applyFilters } = useOpenItemsFilterSections('client_activity')
  const { colors } = useTheme()

  const listItems = applyFilters(items).map((item) => {
    const navigateToActivities = () => {
      dispatch(setSelectedClientId(item.client.id))
      navigate(CLIENT_ACTIVITIES.route)
    }
    return {
      icon: <NotesIcon size={24} />,
      taskType: (
        <FormattedMessage
          defaultMessage='{count} unreviewed {count, plural, one {activity} other {activities}}'
          description='Number of unreviewed client activities'
          values={{ count: item.unreviewedCount }}
        />
      ),
      title: getClientFullName(item.client, isPreferredNameEnabled),
      rightText: item.date
        ? intl.formatMessage(
            {
              defaultMessage: 'Next session: {date}',
              description: 'Next scheduled session date for the client',
            },
            {
              date: intl.formatDate(item.date, { month: 'numeric', day: 'numeric', year: 'numeric' }),
            },
          )
        : null,
      testId: tID('ClientActivityPanel-item'),
      hoverActions: [
        ...getSharedHoverActions(item.client),
        {
          id: 'view-activities',
          icon: <ExerciseIcon size={24} fillColor={colors.iconDefault} />,
          label: intl.formatMessage({
            defaultMessage: 'Go to activities',
            description: 'Hover action to view client activities',
          }),
          testId: tID('ClientActivityPanel-view-activities'),
          onPress: () => {
            track({
              event: mixpanelEvents.BUTTON_PRESS,
              action: mixpanelActions.OPEN_ITEMS_HOVER_ACTION,
              properties: { panel: 'client_activity', hover_action: 'view_activities', origin: 'hover' },
            })
            navigateToActivities()
          },
        },
      ],
      onPress: () => {
        track({
          event: mixpanelEvents.BUTTON_PRESS,
          action: mixpanelActions.OPEN_ITEMS_ROW_PRESS,
          properties: { panel: 'client_activity', task_type: 'client_activity' },
        })
        navigateToActivities()
      },
    }
  })

  useEffect(() => {
    onItemCountChange(listItems.length)
  }, [listItems.length, onItemCountChange])

  return (
    <TaskListPanel
      filterSections={sharedSections}
      items={listItems}
      isLoading={isLoading}
      loadingMessage={
        <FormattedMessage defaultMessage='Loading client activity...' description='Loading state message' />
      }
      filteredEmptyMessage={
        <FormattedMessage
          defaultMessage='No client activity found, remove filters to see more.'
          description='Filtered empty state message'
        />
      }
      onClearFilters={clearSharedFilters}
    />
  )
}

export default ClientActivityPanel
